import emptyStateImage from 'Assets/images/empy-states/empty-conversations-list.png';
import InfiniteList from 'Components/shared/InfiniteList';
import { isAfter } from 'date-fns';
import { useFetchConversations } from 'Modules/conversation/conversations-list/requests/index.requests';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router';
import { getCurrentConversationId } from 'Utils/getCurrentConversationId';
import { routeChecks } from 'Utils/routeUtils';
import { cn } from '~/lib/utils';
import ConversationItem from './ConversationItem';
import { NewConversationPlaceholder } from './NewConversationPlaceholder';

const ConversationsPanelContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isCreatingNewConversation = routeChecks.isNewConversationPage();

  const {
    conversationsList,
    isLoadingConversationsList,
    hasMoreConversations,
    previewConversationId,
    isAdHocGroupCreation,
    pinnedConversations,
    conversationsListFilter,
  } = useConversationStore();
  const { refetch } = useFetchConversations();

  const hasCurrentConversation = React.useRef(false);

  const handleScroll = React.useCallback(() => {
    if (isLoadingConversationsList || !hasMoreConversations) {
      return;
    }

    void refetch();
  }, [hasMoreConversations, isLoadingConversationsList, refetch]);

  React.useEffect(() => {
    if (getCurrentConversationId(location.pathname)) {
      hasCurrentConversation.current = true;
      return;
    }

    if (
      (conversationsList.length === 0 && pinnedConversations.length === 0) ||
      hasCurrentConversation.current
    ) {
      return;
    }

    const mostRecentConversation =
      conversationsList[0]?.lastMessageDate || conversationsList[0]?.created;
    const mostRecentPinnedConversation =
      pinnedConversations[0]?.lastMessageDate ||
      pinnedConversations[0]?.created;

    const mostRecentConversationId = isAfter(
      mostRecentPinnedConversation,
      mostRecentConversation
    )
      ? pinnedConversations[0]?.id
      : conversationsList[0]?.id;

    if (mostRecentConversationId && !isCreatingNewConversation) {
      hasCurrentConversation.current = true;
      void navigate(`/chat/conversations/${mostRecentConversationId}/menu`);
    }
  }, [
    conversationsList,
    location.pathname,
    navigate,
    pinnedConversations,
    pinnedConversations.length,
    isCreatingNewConversation,
  ]);

  const itemsToRender = React.useMemo(() => {
    return conversationsList
      .filter(
        (conversation) =>
          conversation &&
          (conversation.lastMessageId ||
            conversation.grouping === 'Channel' ||
            conversation.grouping === 'Group')
      )
      .map((conversation) => (
        <ConversationItem key={conversation.id} conversation={conversation} />
      ));
  }, [conversationsList]);

  // Determine if we should show empty state
  // Show empty state only if:
  // 1. No conversations in list AND
  // 2. No preview conversation AND
  // 3. Not creating ad-hoc group AND
  // 4. For archived filter, also no pinned conversations (since archived don't show pinned)
  const shouldShowEmptyState = React.useMemo(() => {
    const hasConversationsInList = conversationsList.length > 0;
    const hasPreviewConversation = !!previewConversationId;
    const isCreatingAdHoc = isAdHocGroupCreation;
    const hasPinnedConversations =
      conversationsListFilter === 'archived'
        ? false
        : pinnedConversations.length > 0;

    return (
      !hasConversationsInList &&
      !hasPreviewConversation &&
      !isCreatingAdHoc &&
      !hasPinnedConversations
    );
  }, [
    conversationsList.length,
    previewConversationId,
    isAdHocGroupCreation,
    conversationsListFilter,
    pinnedConversations.length,
  ]);

  const nonPaginatedItems = React.useMemo<React.ReactNode[]>(() => {
    const items: React.ReactNode[] = [];

    // Add placeholder if creating new conversation
    if (isCreatingNewConversation) {
      items.push(
        <NewConversationPlaceholder key="new-conversation-placeholder" />
      );
    }

    // Add pinned conversations
    if (conversationsListFilter !== 'archived') {
      pinnedConversations.forEach((conversation) => {
        items.push(
          <ConversationItem key={conversation.id} conversation={conversation} />
        );
      });
    }

    return items;
  }, [conversationsListFilter, isCreatingNewConversation, pinnedConversations]);

  return (
    <InfiniteList
      onScroll={handleScroll}
      isLoading={isLoadingConversationsList}
      emptyState={
        shouldShowEmptyState ? (
          <div
            className={cn(
              'flex flex-col items-center text-[13px] leading-[18px] text-foreground'
            )}
          >
            <img src={emptyStateImage} width={205} alt="No conversations" />
            <div>No conversations match this filter.</div>
          </div>
        ) : null
      }
    >
      {nonPaginatedItems}
      {itemsToRender}
    </InfiniteList>
  );
};

export default ConversationsPanelContent;
